import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Check from 'scaleflow/components/tailwind-icons/check';
import Clipboard from 'scaleflow/components/tailwind-icons/clipboard';

export interface AtomsCopyButtonSignature {
  Element: HTMLButtonElement;
  Args: {
    Named: {
      /** Text placed on the clipboard. */
      value: string;
      /** Optional visible label; the icon alone is used when omitted. */
      label?: string;
    };
  };
}

const CONFIRMATION_MS = 2500;

/**
 * Copy-to-clipboard button.
 *
 * Four places in this app each hand-rolled `navigator.clipboard.writeText`
 * before this existed; this is the shared one. The transient
 * icon-swap-then-revert follows `Chat::BotMessage`, with two differences worth
 * keeping:
 *
 * - the timer is cancelled on teardown, because setting a `@tracked` field
 *   after the component is destroyed throws;
 * - the write is a promise that genuinely rejects (denied permission, or any
 *   non-secure context), so failure is surfaced rather than silently looking
 *   like success.
 */
export default class AtomsCopyButton extends Component<AtomsCopyButtonSignature> {
  @tracked isCopied = false;
  @tracked hasFailed = false;

  private revertTimeout: ReturnType<typeof setTimeout> | null = null;

  @action
  async copy() {
    if (this.revertTimeout) clearTimeout(this.revertTimeout);

    try {
      await navigator.clipboard.writeText(this.args.value);
      this.isCopied = true;
      this.hasFailed = false;
    } catch {
      // Nothing actionable for the user beyond selecting the text by hand, so
      // say so rather than pretending the copy worked.
      this.isCopied = false;
      this.hasFailed = true;
    }

    this.revertTimeout = setTimeout(() => {
      this.isCopied = false;
      this.hasFailed = false;
      this.revertTimeout = null;
    }, CONFIRMATION_MS);
  }

  willDestroy() {
    super.willDestroy();
    if (this.revertTimeout) clearTimeout(this.revertTimeout);
    this.revertTimeout = null;
  }

  <template>
    <button
      type="button"
      class="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      data-test-copy-button
      data-test-copied={{if this.isCopied "true" "false"}}
      {{on "click" this.copy}}
      ...attributes
    >
      {{#if this.isCopied}}
        <Check class="w-3.5 h-3.5 text-green-600" />
        {{t "manual-uploads.copied"}}
      {{else if this.hasFailed}}
        <Clipboard class="w-3.5 h-3.5 text-red-600" />
        {{t "manual-uploads.copy-failed"}}
      {{else}}
        <Clipboard class="w-3.5 h-3.5 text-gray-500" />
        {{if @label @label (t "dictionary.copy")}}
      {{/if}}
    </button>
  </template>
}
